import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import {
    AddLtiIntegration,
    EditLtiIntegration,
    TestLtiIntegration,
} from "../../../services/LtiIntegrationService";
import { useHistory, useParams } from "react-router-dom";
import { usePermissionGuard } from "../../../hooks/usePermissionGuard";
import hasPermission from "../../../utils/hasMultiplePermission";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";

/**
 * Converts stored tool_redirect_uris JSON or text into textarea lines.
 * @param {Array|string} value Stored redirect URIs.
 * @returns {string} Newline-separated URIs
 */
const getToolRedirectUrisText = (value) => {
    if (Array.isArray(value)) {
        return value
            .map((item) => String(item || "").trim())
            .filter(Boolean)
            .join("\n");
    }

    const raw = String(value || "").trim();
    if (!raw) {
        return "";
    }

    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return parsed
                .map((item) => String(item || "").trim())
                .filter(Boolean)
                .join("\n");
        }
    } catch (error) {
        // Keep original textarea text when value is already plain text.
    }

    return raw;
};

/**
 * Renders the create or edit form for one LTI integration.
 * @param {string} mode "create" or "open".
 * @param {string|number} [integrationId] Integration id when editing.
 * @param {object} [initialData] Saved integration values.
 * @param {boolean} [loading=false] Shows the details skeleton while true.
 * @param {Function} [onSaved] Called after a successful save.
 * @returns {JSX.Element} Integration form
 */
const IntegrationForm = ({ mode, integrationId, initialData, loading = false, onSaved }) => {
    const [testing, setTesting] = useState(false);
    const { tab } = useParams();
    const history = useHistory();
    const givenPermissions = useSelector((state) => state.givenPermission);
    const isEdit = tab === "open";

    const canAddIntegration = hasPermission({
        scopes: ["integrationsadd"],
        permissions: givenPermissions,
    });
    const canEditIntegration = hasPermission({
        scopes: ["integrationsedit"],
        permissions: givenPermissions,
    });

    usePermissionGuard({
        scopes: isEdit ? ["integrationsview"] : ["integrationsadd"],
        onNoAccess: () => {
            history.push("/noaccess");
        },
        dependencies: [integrationId, isEdit],
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            integration_name: initialData?.integration_name || "",
            client_id: initialData?.client_id || "",
            service_endpoint: initialData?.service_endpoint || "",
            public_keyset: initialData?.public_keyset || "",
            initiate_login_url: initialData?.initiate_login_url || "",
            redirect_url: initialData?.redirect_url || "",
            tool_redirect_uris: getToolRedirectUrisText(
                initialData?.tool_redirect_uris,
            ),
        },
        validationSchema: Yup.object({
            integration_name: Yup.string().required(
                "Integration Name is required",
            ),
            service_endpoint: Yup.string().when([], {
                is: () => isEdit,
                then: Yup.string()
                    .required("Service Endpoint is required")
                    .matches(
                        /^https?:\/\/.+/i,
                        "Must be a valid URL",
                    ),
                otherwise: Yup.string()
                    .nullable()
                    .matches(
                        /^$|^https?:\/\/.+/i,
                        "Must be a valid URL",
                    ),
            }),
            public_keyset: Yup.string()
                .test(
                    "url-or-key",
                    "Must be a valid URL",
                    (value) => {
                        const trimmedValue = String(value || "").trim();
                        if (!trimmedValue || !/^https?:\/\//i.test(trimmedValue)) {
                            return true;
                        }

                        return /^https?:\/\/.+/i.test(trimmedValue);
                    },
                )
                .when([], {
                    is: () => isEdit,
                    then: (schema) => schema.required("Public Keyset is required"),
                    otherwise: (schema) => schema.nullable(),
                }),
            initiate_login_url: Yup.string().when([], {
                is: () => isEdit,
                then: Yup.string()
                    .required("Initiate Login URL is required")
                    .matches(
                        /^https?:\/\/.+/i,
                        "Must be a valid URL",
                    ),
                otherwise: Yup.string()
                    .nullable()
                    .matches(
                        /^$|^https?:\/\/.+/i,
                        "Must be a valid URL",
                    ),
            }),
            redirect_url: Yup.string().when([], {
                is: () => isEdit,
                then: Yup.string()
                    .required("Platform Return URL is required")
                    .matches(
                        /^https?:\/\/.+/i,
                        "Must be a valid URL",
                    ),
                otherwise: Yup.string()
                    .nullable()
                    .matches(
                        /^$|^https?:\/\/.+/i,
                        "Must be a valid URL",
                    ),
            }),
            tool_redirect_uris: Yup.string()
                .test(
                    "http-https-urls",
                    "Must be a valid URL",
                    (value) =>
                        !value ||
                        value
                            .split(/\r?\n/)
                            .every(
                                (line) =>
                                    !line.trim() ||
                                    /^https?:\/\/.+/i.test(line.trim()),
                            ),
                )
                .when([], {
                    is: () => isEdit,
                    then: (schema) =>
                        schema.required("Tool Redirect URIs are required"),
                    otherwise: (schema) => schema.nullable(),
                }),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            if (isEdit ? !canEditIntegration : !canAddIntegration) {
                return;
            }

            try {
                setSubmitting(true);
                let response;

                const payload = {
                    integration_name: values.integration_name,
                    service_endpoint: values.service_endpoint,
                    public_keyset: values.public_keyset,
                    initiate_login_url: values.initiate_login_url,
                    redirect_url: values.redirect_url,
                    tool_redirect_uris: values.tool_redirect_uris,
                };

                if (mode === "open") {
                    response = await EditLtiIntegration({
                        id: integrationId,
                        ...payload,
                    });
                } else {
                    response = await AddLtiIntegration(payload);
                }

                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: "Integration saved successfully.",
                });
                onSaved(response?.data?.integration || null);
            } catch (error) {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text:
                        error?.response?.data?.message ||
                        "Something Went Wrong!.",
                });
            } finally {
                setSubmitting(false);
            }
        },
    });

    const isSaveDisabled =
        formik.isSubmitting ||
        testing ||
        (isEdit ? !canEditIntegration : !canAddIntegration);

    const requiredFieldNames = [
        "integration_name",
        "service_endpoint",
        "public_keyset",
        "initiate_login_url",
        "redirect_url",
        "tool_redirect_uris",
    ];

    const hasEmptyRequiredFields = requiredFieldNames.some(
        (fieldName) => !String(formik.values[fieldName] || "").trim(),
    );

    const isTestDisabled =
        !canEditIntegration ||
        testing ||
        formik.isSubmitting ||
        hasEmptyRequiredFields ||
        formik.dirty;

    /**
     * Runs the backend test connection for the open integration and shows the result.
     * @returns {Promise<void>}
     */
    const handleTest = async () => {
        if (!integrationId) {
            Swal.fire({
                icon: "info",
                title: "Save first",
                text: "Please save integration before testing.",
            });
            return;
        }

        try {
            setTesting(true);
            const response = await TestLtiIntegration(integrationId);
            const data = response?.data || {};
            const warnings = data.warnings || [];
            const isSuccess = String(data.message || "")
                .toLowerCase()
                .includes("successful");

            let text = data.message || "Integration test successful.";
            if (typeof data.login_initiated === "boolean") {
                text += `\n\nLTI login initiation: ${data.login_initiated ? "OK" : "Failed"}`;
            }
            if (typeof data.resources_count === "number") {
                text += `\nResources: ${data.resources_count}`;
            }
            if (warnings.length) {
                text += `\n\n${warnings.join("\n")}`;
            }

            Swal.fire({
                icon: warnings.length && !isSuccess ? "warning" : "success",
                title:
                    warnings.length && !isSuccess
                        ? "Partial Success"
                        : "Success",
                text,
            });
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    error?.response?.data?.message ||
                    "Integration test failed.",
            });
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className="card ">
            <div className="card-body">
                <div className="card-body-inr card-body-info">
                    {loading ? (
                        <SkeletonTicketList />
                    ) : (
                        <form onSubmit={formik.handleSubmit}>
                            <div className="row">
                                <div
                                    className="col-md-6 col-lg-4"
                                    title="Integration Name"
                                >
                                    <div className="form-group">
                                        <label htmlFor="integration_name">
                                            Integration Name *
                                        </label>
                                        <div className="form-icon-group mb-1">
                                            <input
                                                type="text"
                                                className={
                                                    "form-control" +
                                                    (formik.errors
                                                        .integration_name &&
                                                        formik.touched.integration_name
                                                        ? " is-invalid"
                                                        : "")
                                                }
                                                id="integration_name"
                                                name="integration_name"
                                                placeholder="Integration Name"
                                                value={
                                                    formik.values.integration_name
                                                }
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                        </div>
                                        <small className="form-text text-muted d-block mb-4 mt-1">
                                            Display name for this LTI tool in AIE.
                                        </small>
                                        {formik.errors.integration_name &&
                                            formik.touched.integration_name ? (
                                            <span className="exclamation">
                                                <i className="fal fa-exclamation-circle"></i>
                                            </span>
                                        ) : null}
                                    </div>
                                </div>

                                <div
                                    className="col-md-6 col-lg-4"
                                    title="Client ID"
                                >
                                    <div className="form-group">
                                        <label htmlFor="client_id">
                                            Client (client_id)
                                        </label>
                                        <div className="form-icon-group mb-1">
                                            <input
                                                type="text"
                                                className="form-control block-action"
                                                id="client_id"
                                                name="client_id"
                                                value={formik.values.client_id}
                                                disabled
                                                placeholder="Auto-generated Client ID"
                                            />
                                        </div>
                                        <small className="form-text text-muted d-block mb-4 mt-1">
                                            Generated by AIE — share with the Tool for registration.
                                        </small>
                                    </div>
                                </div>

                                <div
                                    className="col-md-6 col-lg-4"
                                    title="Service Endpoint"
                                >
                                    <div className="form-group">
                                        <label htmlFor="service_endpoint">
                                            Service Endpoint{isEdit ? " *" : ""}
                                        </label>
                                        <div className="form-icon-group mb-1">
                                            <input
                                                type="text"
                                                className={
                                                    "form-control" +
                                                    (formik.errors
                                                        .service_endpoint &&
                                                        formik.touched.service_endpoint
                                                        ? " is-invalid"
                                                        : "")
                                                }
                                                id="service_endpoint"
                                                name="service_endpoint"
                                                placeholder="Service Endpoint"
                                                value={
                                                    formik.values.service_endpoint
                                                }
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                        </div>
                                        <small className="form-text text-muted d-block mb-4 mt-1">
                                            Tool launch URL (HTTPS).
                                        </small>
                                        {formik.errors.service_endpoint &&
                                            formik.touched.service_endpoint ? (
                                            <span className="exclamation">
                                                <i className="fal fa-exclamation-circle"></i>
                                            </span>
                                        ) : null}
                                    </div>
                                </div>

                                <div
                                    className="col-md-6 col-lg-4"
                                    title="Public Keyset"
                                >
                                    <div className="form-group">
                                        <label htmlFor="public_keyset">
                                            Public Keyset{isEdit ? " *" : ""}
                                        </label>
                                        <div className="form-icon-group mb-1">
                                            <textarea
                                                className={
                                                    "form-control" +
                                                    (formik.errors.public_keyset &&
                                                        formik.touched.public_keyset
                                                        ? " is-invalid"
                                                        : "")
                                                }
                                                rows="4"
                                                id="public_keyset"
                                                name="public_keyset"
                                                placeholder="Public Keyset"
                                                value={formik.values.public_keyset}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                        </div>
                                        <small className="form-text text-muted d-block mb-4 mt-1">
                                            Tool JWKS URL or public key PEM/JSON.
                                        </small>
                                        {formik.errors.public_keyset &&
                                            formik.touched.public_keyset ? (
                                            <span className="exclamation">
                                                <i className="fal fa-exclamation-circle"></i>
                                            </span>
                                        ) : null}
                                    </div>
                                </div>

                                <div
                                    className="col-md-6 col-lg-4"
                                    title="Initiate Login URL"
                                >
                                    <div className="form-group">
                                        <label htmlFor="initiate_login_url">
                                            Initiate Login URL{isEdit ? " *" : ""}
                                        </label>
                                        <div className="form-icon-group mb-1">
                                            <input
                                                type="text"
                                                className={
                                                    "form-control" +
                                                    (formik.errors
                                                        .initiate_login_url &&
                                                        formik.touched
                                                            .initiate_login_url
                                                        ? " is-invalid"
                                                        : "")
                                                }
                                                id="initiate_login_url"
                                                name="initiate_login_url"
                                                placeholder="Initiate Login URL"
                                                value={
                                                    formik.values.initiate_login_url
                                                }
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                        </div>
                                        <small className="form-text text-muted d-block mb-4 mt-1">
                                            Tool OIDC login URL (HTTPS).
                                        </small>
                                        {formik.errors.initiate_login_url &&
                                            formik.touched.initiate_login_url ? (
                                            <span className="exclamation">
                                                <i className="fal fa-exclamation-circle"></i>
                                            </span>
                                        ) : null}
                                    </div>
                                </div>

                                <div
                                    className="col-md-6 col-lg-4"
                                    title="Platform return URL (optional). Tool OIDC form_post destinations belong in Tool Redirect URIs."
                                >
                                    <div className="form-group">
                                        <label htmlFor="redirect_url">
                                            Platform Return URL{isEdit ? " *" : ""}
                                        </label>
                                        <div className="form-icon-group mb-1">
                                            <input
                                                type="text"
                                                className={
                                                    "form-control" +
                                                    (formik.errors.redirect_url &&
                                                        formik.touched.redirect_url
                                                        ? " is-invalid"
                                                        : "")
                                                }
                                                id="redirect_url"
                                                name="redirect_url"
                                                placeholder="Redirect URL"
                                                value={formik.values.redirect_url}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                        </div>
                                        <small className="form-text text-muted d-block mb-4 mt-1">
                                            AIE return URL after Tool interaction.
                                        </small>
                                        {formik.errors.redirect_url &&
                                            formik.touched.redirect_url ? (
                                            <span className="exclamation">
                                                <i className="fal fa-exclamation-circle"></i>
                                            </span>
                                        ) : null}
                                    </div>
                                </div>

                                <div
                                    className="col-md-6 col-lg-4"
                                    title="Tool OIDC redirect URIs where AIE form_posts the id_token (one HTTPS URI per line)"
                                >
                                    <div className="form-group">
                                        <label htmlFor="tool_redirect_uris">
                                            Tool Redirect URIs{isEdit ? " *" : ""}
                                        </label>
                                        <div className="form-icon-group mb-1">
                                            <textarea
                                                className={
                                                    "form-control" +
                                                    (formik.errors
                                                        .tool_redirect_uris &&
                                                        formik.touched
                                                            .tool_redirect_uris
                                                        ? " is-invalid"
                                                        : "")
                                                }
                                                rows="4"
                                                id="tool_redirect_uris"
                                                name="tool_redirect_uris"
                                                placeholder="One HTTPS URI per line"
                                                value={
                                                    formik.values.tool_redirect_uris
                                                }
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                        </div>
                                        <small className="form-text text-muted d-block mb-4 mt-1">
                                            Allowed Tool redirect URIs (one HTTPS URI per line).
                                        </small>
                                        {formik.errors.tool_redirect_uris &&
                                            formik.touched.tool_redirect_uris ? (
                                            <span className="exclamation">
                                                <i className="fal fa-exclamation-circle"></i>
                                            </span>
                                        ) : null}
                                    </div>
                                </div>
                            </div>

                            <div className="form-group form-group-save-cancel">
                                {tab !== "create" && (
                                    <button
                                        className="btn btn-white-bordered"
                                        type="button"
                                        title="Test Integration"
                                        onClick={handleTest}
                                        disabled={isTestDisabled}
                                    >
                                        {testing ? (
                                            <i className="fas fa-cog fa-spin"></i>
                                        ) : (
                                            <i className="fal fa-vial"></i>
                                        )}
                                        Test Integration
                                    </button>
                                )}

                                <button
                                    className="btn btn-save btn-success"
                                    type="submit"
                                    title="Save"
                                    disabled={isSaveDisabled}
                                >
                                    {formik.isSubmitting ? (
                                        <i className="fas fa-cog fa-spin"></i>
                                    ) : (
                                        <i className="fal fa-save"></i>
                                    )}
                                    Save
                                </button>

                                <button
                                    className="btn btn-close btn-danger"
                                    type="button"
                                    title="Cancel"
                                    onClick={() =>
                                        tab === "create"
                                            ? history.goBack()
                                            : formik.resetForm()
                                    }
                                >
                                    <i className="fal fa-times"></i>
                                    Cancel
                                </button>
                            </div>

                            {/* Automatic Validation Messages */}
                            {Object.keys(formik.initialValues).map((key) =>
                                formik.touched[key] && formik.errors[key] ? (
                                    <div
                                        key={key}
                                        className="invalid-feedback d-block"
                                    >
                                        {formik.errors[key]}
                                    </div>
                                ) : null,
                            )}
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IntegrationForm;
